import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const PreviewPicker = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const { startPreview } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => api.get("/preview/borrowers", { params: { search: q } }).then((r) => setItems(r.data.items)), 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const pick = async (b) => {
    setBusy(b.id);
    try {
      const { data } = await api.post(`/preview/${b.id}/start`);
      startPreview({ id: data.id, name: data.name });
      setOpen(false);
      onNavigate?.();
      navigate("/portal");
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <button onClick={() => { setQ(""); setItems(null); setOpen(true); }} data-testid="view-borrower-portal-button"
        className="sidebar-link flex w-full items-center gap-2 rounded-md border border-[hsl(var(--sidebar-border))] px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em]">
        <Eye className="h-4 w-4" /> View borrower portal
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="preview-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">View borrower portal</DialogTitle>
            <DialogDescription>Pick a borrower to see their portal exactly as they do. Read-only — nothing can be changed.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-10 pl-9" data-testid="preview-search-input" />
          </div>
          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border" data-testid="preview-borrower-list">
            {items?.map((b) => (
              <li key={b.id}>
                <button onClick={() => pick(b)} disabled={!!busy} data-testid={`preview-pick-${b.email}`}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{b.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{b.email}</span>
                  </span>
                  {busy === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </button>
              </li>
            ))}
            {items && !items.length && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No borrowers found.</li>}
            {!items && <li className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</li>}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
};
